import React from 'react';
import { Box, Container, Skeleton } from '@mui/material';

const statColors = [
  'rgba(102, 126, 234, 0.12)',
  'rgba(16, 185, 129, 0.12)',
  'rgba(245, 158, 11, 0.14)',
  'rgba(59, 130, 246, 0.12)',
];

const columnTemplate = '28% 13% 10% 9% 22% 10% 8%';

const AlertsSkeleton = () => (
  <Box sx={{ width: '100%' }}>
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Skeleton variant="rounded" width={38} height={38} animation="wave" sx={{ borderRadius: 2 }} />
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.35, flexWrap: 'wrap' }}>
              <Skeleton variant="text" width={162} height={30} animation="wave" />
              <Skeleton variant="rounded" width={72} height={20} animation="wave" sx={{ borderRadius: 5 }} />
            </Box>
            <Skeleton variant="text" width={300} height={18} animation="wave" />
          </Box>
        </Box>
        <Skeleton variant="rounded" width={108} height={32} animation="wave" sx={{ borderRadius: 1.5 }} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5, mb: 2 }}>
        {statColors.map((bg, index) => (
          <Box
            key={index}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 2,
              border: '1px solid #e8edf5',
              bgcolor: '#fff',
              p: '12px 14px',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 60,
                height: 60,
                background: `radial-gradient(circle at top right, ${bg} 0%, transparent 70%)`,
              }}
            />
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
              <Skeleton variant="rounded" width={34} height={34} animation="wave" sx={{ borderRadius: 1.5, bgcolor: bg, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton variant="text" width="50%" height={14} animation="wave" sx={{ mb: 0.35 }} />
                <Skeleton variant="text" width="34%" height={28} animation="wave" sx={{ mb: 0.12 }} />
                <Skeleton variant="text" width="56%" height={14} animation="wave" />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
          <Skeleton
            variant="rounded"
            width={280}
            height={32}
            animation="wave"
            sx={{ borderRadius: 1.5, minWidth: { xs: '100%', sm: 280 } }}
          />
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {[76, 72, 88, 64].map((width, index) => (
              <Skeleton key={index} variant="rounded" width={width} height={26} animation="wave" sx={{ borderRadius: 20 }} />
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', ml: { xs: 0, sm: 'auto' } }}>
          <Skeleton variant="rounded" width={164} height={32} animation="wave" sx={{ borderRadius: 1.5 }} />
          <Skeleton variant="rounded" width={118} height={32} animation="wave" sx={{ borderRadius: 1.5 }} />
          <Skeleton variant="rounded" width={72} height={32} animation="wave" sx={{ borderRadius: 1.5 }} />
        </Box>
      </Box>

      <Box
        sx={{
          border: '1px solid #e2e8f0',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#fff',
          boxShadow: '0 10px 24px rgba(15, 23, 42, 0.04), 0 1px 2px rgba(15, 23, 42, 0.04)',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: columnTemplate,
            px: 2,
            py: 1.1,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            minWidth: 980,
          }}
        >
          {[150, 88, 68, 56, 120, 64, 54].map((width, index) => (
            <Skeleton key={index} variant="text" width={width} height={16} animation="wave" />
          ))}
        </Box>

        <Box sx={{ minWidth: 980 }}>
          {Array.from({ length: 8 }).map((_, index) => (
            <Box
              key={index}
              sx={{
                display: 'grid',
                gridTemplateColumns: columnTemplate,
                alignItems: 'center',
                px: 2,
                py: 1.1,
                borderBottom: index < 7 ? '1px solid #eef2f7' : 'none',
                bgcolor: index % 2 === 0 ? '#fff' : 'rgba(99, 102, 241, 0.02)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.1, pr: 1 }}>
                <Skeleton variant="circular" width={15} height={15} animation="wave" sx={{ mt: 0.3, flexShrink: 0 }} />
                <Box sx={{ width: '100%', minWidth: 0 }}>
                  <Skeleton variant="text" width="24%" height={18} animation="wave" sx={{ mb: 0.2 }} />
                  <Skeleton variant="text" width="58%" height={16} animation="wave" />
                </Box>
              </Box>

              <Box sx={{ pr: 1 }}>
                <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.45 }}>
                    <Skeleton variant="circular" width={5} height={5} animation="wave" />
                    <Skeleton variant="text" width={54} height={16} animation="wave" />
                  </Box>
                  <Skeleton variant="text" width={86} height={14} animation="wave" />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55, pr: 1 }}>
                <Skeleton variant="circular" width={5} height={5} animation="wave" />
                <Skeleton variant="text" width={74} height={16} animation="wave" />
              </Box>

              <Box sx={{ textAlign: 'right', pr: 1.5 }}>
                <Skeleton variant="text" width={34} height={18} animation="wave" sx={{ ml: 'auto', mb: 0.2 }} />
                <Skeleton variant="text" width={52} height={14} animation="wave" sx={{ ml: 'auto' }} />
              </Box>

              <Box sx={{ pr: 1.5 }}>
                <Skeleton variant="text" width={`${74 - (index % 3) * 10}%`} height={17} animation="wave" sx={{ mb: 0.2 }} />
                <Skeleton variant="text" width={`${56 - (index % 2) * 12}%`} height={15} animation="wave" />
              </Box>

              <Skeleton variant="text" width="70%" height={16} animation="wave" />
              <Skeleton variant="text" width="62%" height={16} animation="wave" />
            </Box>
          ))}
        </Box>
      </Box>
    </Container>
  </Box>
);

export default AlertsSkeleton;

